import styled from "styled-components"
import { Tr, Td } from "~styled/table"
import SkeletonWrapper from "~components/SkeletonWrapper"
import { Redemption } from "~shapes/redemption"
import { CompanyLogo } from "~components/companies/styled/company"
import { getFullImagePath } from "~utils/utils"
import moment from "moment"
import colors from "~constants/colors"
import {
  PERK_REDEMPTION_STATUS,
  REDEMPTION_STATUS_TO_USER_FACING,
} from "~constants/status"

interface RedemptionRowProps {
  redemption: Redemption
  isLoading: boolean
}

const statusColor: Record<string, string> = {
  [PERK_REDEMPTION_STATUS.REDEEMED]: colors.green,
  [PERK_REDEMPTION_STATUS.INVALIDATED]: colors.red,
  [PERK_REDEMPTION_STATUS.PENDING_EXTERNAL_VALIDATION]: "#F5A623",
  [PERK_REDEMPTION_STATUS.PENDING_EXTERNAL_MANUAL_VALIDATION]: "#F5A623",
  [PERK_REDEMPTION_STATUS.PRE_REDEEMED]: colors.blue2,
}

const RedemptionRow = ({ redemption, isLoading }: RedemptionRowProps) => {
  const userFacingStatus =
    REDEMPTION_STATUS_TO_USER_FACING[redemption.status] || redemption.status
  const chipColor = statusColor[redemption.status] || colors.gray6
  const obc =
    redemption.cclOnboardCreditCurrency || redemption.cclOnboardCreditValue
      ? `${redemption.cclOnboardCreditCurrency || ""} ${redemption.cclOnboardCreditValue || ""}`.trim()
      : "—"

  return (
    <Tr key={redemption.id}>
      <Td>
        <SkeletonWrapper isLoading={isLoading} circle height={32} width={32}>
          <Logo src={getFullImagePath(redemption.perkLogo)} />
        </SkeletonWrapper>
      </Td>
      <Td>
        <SkeletonWrapper isLoading={isLoading} width={140}>
          <PerkName>{redemption.perkName}</PerkName>
        </SkeletonWrapper>
      </Td>
      <Td>
        <SkeletonWrapper isLoading={isLoading} width={70}>
          <CellText>
            {moment(redemption.createdAt, "YYYY-MM-DD HH:mm:ss").format(
              "MMM D, YYYY",
            )}
          </CellText>
        </SkeletonWrapper>
      </Td>
      <Td>
        <SkeletonWrapper isLoading={isLoading} width={60}>
          <CellMono>{redemption.bookingId || "—"}</CellMono>
        </SkeletonWrapper>
      </Td>
      <Td>
        <SkeletonWrapper isLoading={isLoading} width={70}>
          <StatusChip $color={chipColor}>{userFacingStatus}</StatusChip>
        </SkeletonWrapper>
      </Td>
      <Td>
        <SkeletonWrapper isLoading={isLoading} width={50}>
          <CellText>{obc}</CellText>
        </SkeletonWrapper>
      </Td>
      <Td>
        <SkeletonWrapper isLoading={isLoading} width={80}>
          <CellText>{redemption.invalidationReason || "—"}</CellText>
        </SkeletonWrapper>
      </Td>
      <Td>
        <SkeletonWrapper isLoading={isLoading} width={80}>
          <CellText>{redemption.invalidationDetails || "—"}</CellText>
        </SkeletonWrapper>
      </Td>
    </Tr>
  )
}

const Logo = styled(CompanyLogo)`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
`

const PerkName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.gray3};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`

const CellText = styled.div`
  font-size: 13px;
  color: ${colors.gray6};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CellMono = styled.div`
  font-size: 12px;
  font-family: monospace;
  color: ${colors.gray6};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StatusChip = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.white};
  background: ${p => p.$color};
  white-space: nowrap;
`

export default RedemptionRow
